import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AnimatedGradientBackground } from '@/components/common/AnimatedGradientBackground'
import { SlotImage } from '@/features/designer/components/SlotImage'
import { withErrorCode } from '@/lib/errors'
import { useDocumentMeta } from '@/lib/useDocumentMeta'

const DIGITS = [
  { char: '4', color: 'text-brand-orange', delay: 0.0 },
  { char: '0', color: 'text-brand-purple', delay: 0.08 },
  { char: '4', color: 'text-brand-orange', delay: 0.16 },
]

const LINKS = [
  { to: '/', label: 'Home' },
  { to: '/seasons', label: 'Films' },
  { to: '/shop', label: 'Shop' },
  { to: '/connect', label: 'Connect' },
]

export function NotFoundPage() {
  useDocumentMeta('Off the count', "This step isn't in the choreography.")

  return (
    <div className="relative flex min-h-[calc(100vh-4rem)] w-full items-center justify-center overflow-hidden px-4">
      {/* Admin-editable background image; falls back to the brand gradient. */}
      <SlotImage
        slotKey="not-found-background"
        className="absolute inset-0 z-0 h-full w-full"
        alt=""
        fadeIn
        fallback={<AnimatedGradientBackground opacity={0.18} duration={14} />}
      />

      {/* Readability scrim over any background image */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-background/40 via-background/55 to-background/75"
      />

      {/* pointer-events-none lets admins hover-reveal the background's Assign-image button;
          interactive children re-enable pointer events individually. */}
      <div className="pointer-events-none relative z-10 flex flex-col items-center text-center">
        {/* 404 — still, brand-colored, no continuous motion */}
        <div
          aria-label="404 — page not found"
          className="flex items-center gap-2 sm:gap-6"
        >
          {DIGITS.map((d, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.55,
                delay: d.delay,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={`select-none text-[8rem] font-black leading-none tracking-tight sm:text-[14rem] md:text-[18rem] ${d.color}`}
              style={{ textShadow: '0 6px 30px rgba(0,0,0,0.25)' }}
            >
              {d.char}
            </motion.span>
          ))}
        </div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 text-3xl font-bold tracking-tight sm:text-4xl"
        >
          You've stepped offstage.
        </motion.h1>

        {/* Subcopy */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-3 max-w-md text-base text-muted-foreground sm:text-lg"
        >
          Let's get you back in-frame.
        </motion.p>
        <p className="mt-3 text-xs text-muted-foreground">
          {withErrorCode('This page is not available.', 'route/not-found')}
        </p>

        {/* Quick links */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
        >
          {LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="pointer-events-auto group relative rounded-full border border-foreground/20 bg-background/60 px-5 py-2 text-sm font-medium backdrop-blur-sm transition-all hover:border-foreground/60 hover:bg-background/90"
            >
              <span className="relative z-10">{link.label}</span>
            </Link>
          ))}
        </motion.div>
      </div>
    </div>
  )
}
